/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/jsx-fragments */
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import WarningYellowOutline from "@druva-apps/orion-icons/lib/WarningYellowOutline";

import { isFunction, gatherClasses, isString } from "../../lib/utils";
import Icon from "../icon/icon";
import Bouncer from "../bouncer/bouncer";
import Tooltip from "../tooltip/tooltip";
import TextTruncate from "./textTruncate";
import "./labelValue.scss";

const SummaryValue = ({
  value,
  truncateValue,
  truncateLines,
  showMoreButton,
  tooltipConfig
}) => {
  if (truncateValue && isString(value)) {
    return (
      <TextTruncate
        key={value}
        text={value}
        tooltipConfig={tooltipConfig}
        lines={truncateLines}
        showMoreButton={showMoreButton}
      />
    );
  }

  return value;
};

const getContainer = (type, labelValueContainer, primaryIcon, alignIcon) => {
  if (["primary", "secondary", "tertiary"].includes(type)) {
    if (alignIcon === "right") {
      return (
        <Fragment>
          {labelValueContainer}
          {primaryIcon}
        </Fragment>
      );
    }
    return (
      <Fragment>
        {primaryIcon}
        {labelValueContainer}
      </Fragment>
    );
  }
  return <Fragment>{labelValueContainer}</Fragment>;
};

const getLabelContainer = (label, align, labelClass) => {
  return (
    (label && (
      <div
        className={gatherClasses(
          null,
          "oui-lv-row",
          "oui-lv-label",
          `oui-lv-horizontally-align-${align}`,
          labelClass
        )}
      >
        {isFunction(label) ? label() : label}
      </div>
    )) ||
    ""
  );
};

const getPrimaryIcon = (type, icon) => {
  if (["primary", "secondary", "tertiary"].includes(type) && icon) {
    return (
      <div className="oui-lv-col oui-lv-primary-icon">
        {React.isValidElement(icon) ? (
          React.cloneElement(icon, {
            className: gatherClasses(null, "oui-lv-icon", icon.props.className),
            muted: false
          })
        ) : (
          <Icon
            size="md"
            {...{
              ...icon,
              classes: `${
                (icon.classes && `${icon.classes} oui-lv-icon`) || ""
              }`,
              muted: false
            }}
          />
        )}
      </div>
    );
  }
  return "";
};

const getLabelValueContainer = (type, labelHtml, valueHtml, align) => {
  let tempHtml = "";
  switch (type) {
    case "summary":
      tempHtml = (
        <Fragment>
          {labelHtml}
          {valueHtml}
        </Fragment>
      );
      break;
    case "primary":
    case "secondary":
    case "tertiary":
      tempHtml = (
        <Fragment>
          {valueHtml}
          {labelHtml}
        </Fragment>
      );
      break;
    default:
      break;
  }
  return (
    <div
      className={`oui-lv-col oui-lv-label-value-container oui-lv-horizontally-align-${align}`}
    >
      {tempHtml}
    </div>
  );
};

const getValueContainer = (
  type,
  value,
  unit,
  icon,
  align,
  alignIcon,
  renderValue,
  data
) => {
  const { isLoading, isError } = data;
  let iconHtml = "";
  let unitHtml = "";
  let valueFullWidth = false;
  let loaderOrError = null;

  if (isLoading || isError) {
    if (isLoading) {
      loaderOrError = (
        <div className="oui-mt-1">
          <Bouncer gray />
        </div>
      );
    }

    if (isError) {
      loaderOrError = (
        <div className="oui-mt-1">
          <Tooltip text="Error loading data">
            <WarningYellowOutline />
          </Tooltip>
        </div>
      );
    }
  }

  switch (type) {
    case "summary":
      iconHtml =
        (icon && (
          <div className="oui-lv-summary-icon">
            {React.isValidElement(icon) ? (
              React.cloneElement(icon, {
                className: gatherClasses(
                  null,
                  "oui-lv-icon",
                  icon.props.className
                ),
                muted: false
              })
            ) : (
              <Icon
                {...{
                  ...icon,
                  classes: `${
                    (icon.classes && `${icon.classes} oui-lv-icon`) || ""
                  }`,
                  muted: false
                }}
              />
            )}
          </div>
        )) ||
        "";
      break;
    case "primary":
    case "secondary":
    case "tertiary":
      unitHtml = (unit && <div className="oui-lv-unit">{unit}</div>) || "";
      break;
    default:
      break;
  }

  if (
    type === "summary" &&
    ((align === "left" && (!icon || (icon && alignIcon !== "right"))) ||
      (align === "right" && (!icon || (icon && alignIcon === "right"))))
  ) {
    valueFullWidth = true;
  }
  const showBarlowFont =
    type !== "summary" &&
    !isFunction(renderValue) &&
    Number.isFinite(Number(value));

  const valueHtml = (
    <Fragment>
      <div
        className={cx("oui-lv-value", {
          "oui-lv-value-number": showBarlowFont
        })}
        style={valueFullWidth ? { width: "100%" } : {}}
      >
        {isFunction(renderValue) ? (
          renderValue(data)
        ) : type === "summary" ? (
          <SummaryValue
            value={value}
            truncateValue={data.truncateValue}
            truncateLines={data.truncateLines}
            showMoreButton={data.showMoreButton}
            tooltipConfig={data.tooltipConfig}
          />
        ) : (
          value
        )}
      </div>
      {unitHtml}
    </Fragment>
  );

  return (
    <div
      className={`oui-lv-row oui-lv-value-container oui-lv-horizontally-align-${align}`}
    >
      {alignIcon === "right" ? (
        loaderOrError !== null ? (
          loaderOrError
        ) : (
          <Fragment>
            {valueHtml}
            {iconHtml}
          </Fragment>
        )
      ) : loaderOrError !== null ? (
        loaderOrError
      ) : (
        <Fragment>
          {iconHtml}
          {valueHtml}
        </Fragment>
      )}
    </div>
  );
};
const LabelValue = props => {
  const {
    label,
    labelClass,
    value,
    unit,
    type,
    icon,
    align,
    alignIcon,
    renderValue,
    dataTestId
  } = props;
  const valueContainer = getValueContainer(
    type,
    value,
    unit,
    icon,
    align,
    alignIcon,
    renderValue,
    props
  );
  const labelContainer = getLabelContainer(label, align, labelClass);
  const labelValueContainer = getLabelValueContainer(
    type,
    labelContainer,
    valueContainer,
    align
  );
  const primaryIcon = getPrimaryIcon(type, icon);
  const container = getContainer(
    type,
    labelValueContainer,
    primaryIcon,
    alignIcon
  );

  return (
    <div
      data-testid={dataTestId}
      className={gatherClasses(
        props,
        `oui-label-value-container oui-lv-${type} oui-lv-horizontally-align-${align}`
      )}
    >
      {container}
    </div>
  );
};

LabelValue.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  labelClass: PropTypes.string,
  renderValue: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  unit: PropTypes.string,
  type: PropTypes.oneOf(["primary", "secondary", "tertiary", "summary"]),
  icon: PropTypes.shape({
    src: PropTypes.string,
    classes: PropTypes.string
  }),
  align: PropTypes.oneOf(["left", "center", "right"]),
  alignIcon: PropTypes.oneOf(["left", "right"]),
  className: PropTypes.string,
  truncateValue: PropTypes.bool,
  truncateLines: PropTypes.number,
  showMoreButton: PropTypes.bool,
  tooltipConfig: PropTypes.shape({
    placement: PropTypes.oneOf(["auto", "top", "right", "bottom", "left"]),
    textAlign: PropTypes.oneOf(["left", "center", "right"]),
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customRenderer: PropTypes.func
  }),
  dataTestId: PropTypes.string,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool
};

LabelValue.defaultProps = {
  label: "",
  labelClass: "",
  value: "-",
  type: "summary",
  align: "left",
  alignIcon: "left",
  truncateValue: false,
  truncateLines: 3,
  showMoreButton: true,
  tooltipConfig: {},
  dataTestId: "orion-label-value",
  isLoading: false,
  isError: false
};

LabelValue.displayName = "OrionLabelValue";
export default LabelValue;
